import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FileText, CheckCircle, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

import API from '../api_config';

const formatCLP = (amount) => new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'CLP',
  minimumFractionDigits: 0
}).format(amount);

export default function PurchasesPage() {
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  
  const [supplierId, setSupplierId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [docType, setDocType] = useState('order');
  const [docNumber, setDocNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([]);
  
  const [selProduct, setSelProduct] = useState('');
  const [selQty, setSelQty] = useState('');
  const [selCost, setSelCost] = useState('');
  const [barcodeScan, setBarcodeScan] = useState('');

  useEffect(() => {
    fetchAll();
  }, []);

  const handleBarcodeSearch = (code) => {
      // Find product by barcode or SKU
      const product = products.find(p => p.barcode === code || p.sku === code);
      if (product) {
          setSelProduct(product.id);
          setSelCost(product.cost.toString());
          toast.success(`Producto encontrado: ${product.name}`);
          setBarcodeScan('');
          // Optional: focus quantity input?
      } else {
          toast.error('Producto no encontrado');
          setBarcodeScan('');
      }
  };

  const fetchAll = async () => {
    try {
      const res = await axios.get(`${API}/purchases`);
      setPurchases(res.data);
      
      const sRes = await axios.get(`${API}/suppliers`);
      setSuppliers(sRes.data);
      
      const pRes = await axios.get(`${API}/products`);
      setProducts(pRes.data);
      
      const wRes = await axios.get(`${API}/warehouses`);
      setWarehouses(wRes.data);
      if (wRes.data.length > 0) setWarehouseId(wRes.data[0].id);
    } catch (error) {
      toast.error('Error al cargar datos');
    }
  };

  const addProductToOrder = () => {
    if (!selProduct || !selQty || !selCost) {
      toast.error('Completa todos los campos del producto');
      return;
    }

    const product = products.find(p => p.id === selProduct);
    const qty = parseFloat(selQty);
    const cost = parseFloat(selCost);
    
    const newItem = {
      product_id: product.id,
      product_name: product.name,
      quantity: qty,
      cost: cost,
      subtotal: qty * cost
    };

    setItems([...items, newItem]);
    setSelProduct('');
    setSelQty('');
    setSelCost('');
    toast.success('Producto agregado a la orden');
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const createOrder = async () => {
    if (!supplierId) {
      toast.error('Selecciona un proveedor');
      return;
    }
    if (items.length === 0) {
      toast.error('Agrega al menos un producto');
      return;
    }

    try {
      let finalSupplierId = supplierId;

      // Handle automatic creation of "Manual" supplier if placeholder selected
      if (supplierId === 'manual_entry_placeholder') {
          try {
              toast.info('Creando proveedor "Inventario Manual"...');
              const supRes = await axios.post(`${API}/suppliers`, {
                  name: 'Inventario Manual',
                  rut: '99.999.999-9',
                  address: 'Generado Automáticamente'
              });
              finalSupplierId = supRes.data.id;
              
              // Update local state
              setSuppliers([...suppliers, supRes.data]);
              toast.success('Proveedor manual creado');
          } catch (err) {
              console.error(err);
              toast.error('Error al crear proveedor manual');
              return;
          }
      }

      const total = items.reduce((sum, item) => sum + item.subtotal, 0);
      await axios.post(`${API}/purchases`, {
        supplier_id: finalSupplierId,
        warehouse_id: warehouseId,
        items: items,
        total: total,
        document_type: docType,
        document_number: docNumber,
        notes: notes
      });
      
      toast.success('✅ Orden de compra creada exitosamente');
      
      setSupplierId('');
      setNotes('');
      setDocNumber('');
      setDocType('order');
      setItems([]);
      setShowForm(false);
      fetchAll();
    } catch (error) {
      toast.error('Error al crear orden: ' + (error.response?.data?.detail || error.message));
    }
  };

  const receivePurchase = async (purchaseId) => {
    try {
      await axios.post(`${API}/purchases/${purchaseId}/receive`);
      toast.success('✅ Compra recibida - Inventario actualizado en la bodega');
      fetchAll();
    } catch (error) {
      toast.error('Error al recibir compra');
    }
  };

  const deletePurchase = async (id) => {
    if (!window.confirm('¿Seguro que deseas eliminar este registro? Si ya fue recibido, se descontará del inventario.')) return;
    try {
      await axios.delete(`${API}/purchases/${id}`);
      toast.success('Registro eliminado y stocks ajustados');
      fetchAll();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const total = items.reduce((sum, item) => sum + item.subtotal, 0);

  return (
    <div className="space-y-6" data-testid="purchases-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold">Compras e Inventario</h1>
          <p className="text-muted-foreground mt-2">Órdenes de compra y recepción</p>
        </div>
        <Button 
          onClick={() => setShowForm(!showForm)}
          className="rounded-full shadow-lg"
          data-testid="toggle-form-btn"
        >
          {showForm ? <ChevronUp className="h-5 w-5 mr-2" /> : <Plus className="h-5 w-5 mr-2" />}
          {showForm ? 'Ocultar Formulario' : 'Nueva Orden de Compra'}
        </Button>
      </div>

      {showForm && (
        <Card className="border-2 border-primary">
          <CardHeader>
            <CardTitle>📝 Nueva Orden de Compra / Factura</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Proveedor *</Label>
                <select
                  value={supplierId}
                  onChange={(e) => setSupplierId(e.target.value)}
                  className="w-full h-10 rounded-lg border border-input bg-background px-3"
                  data-testid="supplier-select"
                >
                  <option value="">Seleccionar proveedor</option>
                  {suppliers.length === 0 ? (
                      <option value="manual_entry_placeholder" className="text-amber-600 font-semibold">
                          Inventario manual no existe (Crear Automático)
                      </option>
                  ) : (
                      suppliers.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))
                  )}
                  </select>
              </div>

              <div className="space-y-2">
                <Label>Bodega Destino *</Label>
                <select
                  value={warehouseId}
                  onChange={(e) => setWarehouseId(e.target.value)}
                  className="w-full h-10 rounded-lg border border-input bg-background px-3"
                  data-testid="warehouse-select"
                >
                  {warehouses.map(w => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">
                  ⚠️ El inventario se agregará a esta bodega al recepcionar
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               <div className="space-y-2">
                 <Label>Tipo Documento *</Label>
                 <select
                   value={docType}
                   onChange={(e) => setDocType(e.target.value)}
                   className="w-full h-10 rounded-lg border border-input bg-background px-3"
                 >
                   <option value="order">Orden de Compra</option>
                   <option value="invoice">Factura</option>
                 </select>
               </div>
               <div className="space-y-2">
                  <Label>N° Documento</Label>
                  <Input 
                    value={docNumber}
                    onChange={(e) => setDocNumber(e.target.value)}
                    placeholder={docType === 'invoice' ? 'Ej: 12345' : 'Ej: OC-2023-001'}
                  />
               </div>
            </div>

            <div className="border-2 border-dashed rounded-lg p-4 space-y-4 bg-muted/30">
              <h4 className="font-semibold">Agregar Productos</h4>
              
              {/* Barcode Scanner for Purchase */}
              <div className="flex gap-2 mb-2">
                 <Input 
                    placeholder="Escanear Código de Barras..."
                    value={barcodeScan}
                    onChange={(e) => setBarcodeScan(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handleBarcodeSearch(e.currentTarget.value);
                    }}
                    className="flex-1 bg-white dark:bg-black"
                    autoFocus
                 />
              </div>

              <div className="grid grid-cols-12 gap-3">
                <div className="col-span-6">
                  <Label className="text-xs">Producto</Label>
                  <select
                    value={selProduct}
                    onChange={(e) => {
                      setSelProduct(e.target.value);
                      const p = products.find(pr => pr.id === e.target.value);
                      if (p) setSelCost(p.cost.toString());
                    }}
                    className="w-full h-9 rounded-lg border border-input bg-background px-2 text-sm"
                    data-testid="product-select"
                  >
                    <option value="">Seleccionar</option>
                    {products.map(p => (
                      <option key={p.id} value={p.id}>{p.name} - {p.sku}</option>
                    ))}
                  </select>
                </div>

                <div className="col-span-2">
                  <Label className="text-xs">Cantidad</Label>
                  <Input
                    type="number"
                    value={selQty}
                    onChange={(e) => setSelQty(e.target.value)}
                    placeholder="50"
                    className="h-9"
                    min="0.001"
                    step="0.001"
                    data-testid="quantity-input"
                  />
                </div>

                <div className="col-span-2">
                  <Label className="text-xs">Costo Unit.</Label>
                  <Input
                    type="number"
                    value={selCost}
                    onChange={(e) => setSelCost(e.target.value)}
                    placeholder="CLP"
                    className="h-9"
                    min="0"
                    data-testid="cost-input"
                  />
                </div>

                <div className="col-span-2 flex items-end">
                  <Button
                    type="button"
                    onClick={addProductToOrder}
                    className="w-full h-9"
                    data-testid="add-item-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Agregar
                  </Button>
                </div>
              </div>
            </div>

            {items.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-semibold">Productos en la Orden ({items.length})</h4>
                <div className="space-y-2">
                  {items.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 border rounded-lg bg-background">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{item.product_name}</p>
                        <p className="text-xs text-muted-foreground font-mono">
                          {item.quantity} unidades × {formatCLP(item.cost)} = {formatCLP(item.subtotal)}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(idx)}
                        className="text-destructive"
                        data-testid={`remove-item-${idx}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center pt-3 border-t">
                  <span className="text-lg font-semibold">Total de la Orden:</span>
                  <span className="text-2xl font-bold font-mono text-primary">{formatCLP(total)}</span>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Notas / N° Factura (Opcional)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ej: Factura #12345, Pedido urgente..."
                data-testid="notes-input"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowForm(false);
                  setItems([]);
                  setSupplierId('');
                  setNotes('');
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={createOrder}
                disabled={items.length === 0}
                className="shadow-lg"
                data-testid="create-order-btn"
              >
                <CheckCircle className="h-5 w-5 mr-2" />
                {docType === 'invoice' ? 'Crear Factura' : 'Crear Orden de Compra'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200">
        <CardContent className="p-4">
          <h4 className="font-semibold mb-2">💡 ¿Cómo funciona?</h4>
          <ol className="space-y-1 text-sm">
            <li><strong>1.</strong> Crea una orden seleccionando proveedor y bodega destino</li>
            <li><strong>2.</strong> Agrega productos con sus cantidades y costos</li>
            <li><strong>3.</strong> La orden queda en estado "Pendiente" (no afecta inventario aún)</li>
            <li><strong>4.</strong> Click en "Recibir Compra" actualiza el inventario automáticamente en la bodega</li>
          </ol>
        </CardContent>
      </Card>

      <div className="space-y-4">
        <h3 className="text-2xl font-semibold">Órdenes de Compra</h3>
        {purchases.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50 text-muted-foreground" />
              <p className="text-muted-foreground mb-2">No hay órdenes de compra registradas</p>
              <p className="text-sm text-muted-foreground">Crea tu primera orden para gestionar el inventario</p>
            </CardContent>
          </Card>
        ) : (
          purchases.map(purchase => (
            <Card key={purchase.id} className="hover:shadow-md transition-shadow" data-testid={`purchase-${purchase.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <h3 className="font-semibold text-xl">Orden #{purchase.id.slice(0, 8)}</h3>
                      <span className={`px-3 py-1 text-xs rounded-full font-medium ${
                        purchase.status === 'received'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                      }`}>
                        {purchase.status === 'received' ? '✓ Recibida' : '⏳ Pendiente'}
                      </span>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-2">
                      {purchase.items.length} producto(s)
                    </p>
                    
                    <div className="space-y-1 mt-3 text-sm">
                      {purchase.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-muted-foreground">
                          <span>• {item.product_name} ×{item.quantity}</span>
                          <span className="font-mono">{formatCLP(item.subtotal)}</span>
                        </div>
                      ))}
                    </div>
                    
                    <p className="text-xs text-muted-foreground mt-3">
                      {new Date(purchase.created_at).toLocaleString('es-CL')}
                    </p>
                  </div>
                  
                  <div className="text-right ml-6">
                    <p className="font-mono font-bold text-3xl mb-3 text-primary">
                      {formatCLP(purchase.total)}
                    </p>
                    <div className="flex flex-col gap-2 items-end">
                      {purchase.status === 'pending' ? (
                        <Button
                          onClick={() => receivePurchase(purchase.id)}
                          size="lg"
                          className="shadow-lg"
                          data-testid={`receive-btn-${purchase.id}`}
                        >
                          <CheckCircle className="h-5 w-5 mr-2" />
                          Recibir
                        </Button>
                      ) : (
                        <p className="text-sm text-green-600 dark:text-green-400 font-semibold">
                          ✓ Inventario Cargado
                        </p>
                      )}
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deletePurchase(purchase.id)}
                        className="text-destructive hover:bg-destructive/10"
                        title="Eliminar (Revertirá inventario si ya fue recibido)"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Eliminar
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
